import React, {useEffect, useRef, useState} from 'react'
import {useSelector} from 'react-redux';
import {getInvoiceTDS, getBeneficiaryPPATrail} from '../../../helper/Api';
import DataTable from 'react-data-table-component';
import {AddCommasToAmount} from "../../../helper/Utils";
import {useLocation} from "react-router-dom";
import moment from 'moment';
import { Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AiOutlineVerticalAlignBottom, AiOutlineEye } from 'react-icons/ai';
import {
    actionIconBtnStyle,
    isOnlineRow,
    isSelectableForChallan,
} from '../reconPaymentHelpers';
import PaymentTrailModal from '../PaymentTrailModal';
import PaymentModeStatusBadges from '../PaymentModeStatusBadges';
import useReconTableScrollHeight from '../useReconTableScrollHeight';
import './OtherList.css';

const MultiLineHeader = ({ lines, align = 'center' }) => (
    <div className={`other-col-header other-col-header--${align}`}>
        {lines.map((line, idx) => (
            <React.Fragment key={idx}>
                {idx > 0 && <br />}
                {line}
            </React.Fragment>
        ))}
    </div>
);

const CenterCell = ({ children }) => (
    <div className="other-cell-center">{children}</div>
);

const OTHERList = ({setSelectednvoice, fromDate, toDate, searchStr, dateColumn, loading, setLoading}) => {
    const {authData} = useSelector((state) => state.authData);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    const lastFetchKeyRef = useRef('');
    let {state} = useLocation();
    const [data, setData] = useState(state && state.data ? state.data : []);
    const [openModal, setOpenModal] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState([]);
    const tableContainerRef = useRef(null);
    const tableScrollHeight = useReconTableScrollHeight({
        containerRef: tableContainerRef,
        dataLength: data.length,
        loading,
    });

    const fetchChallans = async (page = currentPage, perPage = rowsPerPage) => {
        const fetchKey = [fromDate, toDate, searchStr, dateColumn, page, perPage].join('|');
        if (lastFetchKeyRef.current === fetchKey) return;
        lastFetchKeyRef.current = fetchKey;
        try {
            setLoading(true);
            const data = await getInvoiceTDS({
                from_date: fromDate,
                to_date: toDate,
                searchStr: searchStr,
                dateColumn: dateColumn,
                other: true,
                limit: perPage,
                offset: page,
            });
            if (data.data.status) {
                setData(data.data.list || []);
                setTotalCount(Number(data.data.total_count) || 0);
            } else {
                setData([]);
                setTotalCount(0);
            }
        } catch (error) {
            lastFetchKeyRef.current = '';
            setData([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
        fetchChallans(1, rowsPerPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, toDate, searchStr, dateColumn]);

    const handleDownload = (url) => {
        if (!url) return;
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const fetchPaymentTrail = async ({ ppa_invoice_batch_id, account_no, invoice_id }) => {
        try {
            setLoading(true);
            const response = await getBeneficiaryPPATrail({
                ppa_invoice_batch_id,
                account_no,
                invoice_id,
            });
            if (response?.data) {
                setSelectedRowData(response.data.result || []);
            }
        } catch (error) {
            setSelectedRowData([]);
            toast.error(error?.response?.data?.message || error?.message || 'Failed to fetch payment trail', {
                position: toast.POSITION.TOP_CENTER,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = async (rowData) => {
        await fetchPaymentTrail({
            ppa_invoice_batch_id: rowData.ppa_invoice_batch_id,
            account_no: rowData.reimbursement_beneficiary_account_no || rowData.account_no,
            invoice_id: rowData.invoice_id || rowData.id,
        });
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedRowData([]);
    };

    const customStyles = {
        table: {
            style: {
                width: '100%',
            },
        },
        headRow: {
            style: {
                minHeight: '52px',
            },
        },
        headCells: {
            style: {
                fontWeight: 'bold',
                fontSize: '14px',
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'clip',
                wordBreak: 'break-word',
                lineHeight: 1.35,
                paddingTop: '8px',
                paddingBottom: '8px',
            },
        },
    };

    const columns = [
        {
            name: 'Actions',
            selector: (row) => (
                <div className="d-flex align-items-center justify-content-center" style={{ gap: '6px', width: '100%' }}>
                    <button
                        type="button"
                        title="Download Attachment"
                        disabled={!row?.invoice}
                        onClick={() => handleDownload(row.invoice)}
                        style={{
                            ...actionIconBtnStyle,
                            border: '1px solid #1976d2',
                            color: '#1976d2',
                            cursor: row?.invoice ? 'pointer' : 'not-allowed',
                            opacity: row?.invoice ? 1 : 0.5,
                        }}
                    >
                        <AiOutlineVerticalAlignBottom size={16} />
                    </button>
                    {isOnlineRow(row) && (
                        <button
                            type="button"
                            title="View Payment Trail"
                            onClick={() => handleOpenModal(row)}
                            style={{
                                ...actionIconBtnStyle,
                                border: '1px solid #f59e0b',
                                color: '#f59e0b',
                                cursor: 'pointer',
                            }}
                        >
                            <AiOutlineEye size={16} />
                        </button>
                    )}
                </div>
            ),
            width: '110px',
            minWidth: '110px',
            center: true,
        },
        {
            name: <MultiLineHeader lines={['Beneficiary', 'Details']} align="left" />,
            selector: (row) => (
                <div>
                    <div>{row.company_name}</div>
                    <div>
                        <b>Pan No.:</b> {row.pan_no}
                    </div>
                    <PaymentModeStatusBadges row={row} />
                </div>
            ),
            sortable: true,
            wrap: true,
            minWidth: '240px',
            width: '240px',
        },
        {
            name: <MultiLineHeader lines={['Invoice', 'Details']} />,
            selector: (row) => (
                <CenterCell>
                    <div>{row.invoice_no}</div>
                    <Badge bg="warning" className="mt-1">
                        {moment(row.invoice_date, 'YYYY-MM-DD').format('DD-MM-YYYY')}
                    </Badge>
                </CenterCell>
            ),
            center: true,
            minWidth: '175px',
            width: '175px',
        },
        {
            name: <MultiLineHeader lines={['Voucher', 'Details']} />,
            selector: (row) => (
                <CenterCell>
                    <div>{row.voucher_no}</div>
                    <Badge bg="primary" className="mt-1">
                        {moment(row.voucher_date, 'YYYY-MM-DD').format('DD-MM-YYYY')}
                    </Badge>
                </CenterCell>
            ),
            center: true,
            minWidth: '220px',
            width: '220px',
        },
        {
            name: <MultiLineHeader lines={['Taxable', 'Amount']} />,
            selector: (row) => (
                <CenterCell>
                    {row.taxable_amount ? AddCommasToAmount(row.taxable_amount) : ''}
                </CenterCell>
            ),
            center: true,
            minWidth: '150px',
            width: '150px',
        },
        {
            name: <MultiLineHeader lines={['Sanctioned', 'Amount']} />,
            selector: (row) => (
                <CenterCell>
                    {row.sanction_amount ? AddCommasToAmount(row.sanction_amount) : ''}
                </CenterCell>
            ),
            center: true,
            minWidth: '160px',
            width: '160px',
        },
        {
            name: <MultiLineHeader lines={['Other', 'Deduction']} />,
            selector: (row) => (
                <CenterCell>
                    {row.other_deduction ? AddCommasToAmount(row.other_deduction) : ''}
                </CenterCell>
            ),
            center: true,
            minWidth: '150px',
            width: '150px',
        },
        {
            name: <MultiLineHeader lines={['Challan', 'Details']} />,
            selector: () => <CenterCell>-</CenterCell>,
            center: true,
            minWidth: '180px',
            width: '180px',
        },
        {
            name: '',
            selector: () => '',
            grow: 1,
            minWidth: '1px',
            sortable: false,
            reorder: false,
        },
    ];

    const handleChangeRowsPerPage = (newPerPage, page) => {
        setRowsPerPage(newPerPage);
        setCurrentPage(page);
        fetchChallans(page, newPerPage);
    };

    const handleChangePage = (page) => {
        if (page === currentPage) return;
        setCurrentPage(page);
        fetchChallans(page, rowsPerPage);
    };

    const handleSelectedChange = ({selectedRows}) => {
        setSelectednvoice(selectedRows.filter(isSelectableForChallan));
    };

    return (
        <div ref={tableContainerRef} className="other-datatable-root">
            <PaymentTrailModal
                open={openModal}
                onClose={handleCloseModal}
                trails={selectedRowData}
            />
            <DataTable
                customStyles={customStyles}
                className="dataTables_wrapper"
                selectableRows={authData.user.user_type === 'l1'}
                selectableRowDisabled={(row) => !isSelectableForChallan(row)}
                onSelectedRowsChange={handleSelectedChange}
                columns={columns}
                data={data}
                fixedHeader
                fixedHeaderScrollHeight={tableScrollHeight}
                pagination
                paginationServer
                paginationPerPage={rowsPerPage}
                paginationDefaultPage={currentPage}
                paginationTotalRows={totalCount}
                paginationRowsPerPageOptions={[25, 50, 100]}
                onChangeRowsPerPage={handleChangeRowsPerPage}
                onChangePage={handleChangePage}
            />
        </div>
    );
};

export default OTHERList;
